import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { DropdownInput, useFormContext } from "@validereinc/common-components";
import { EquipmentAdapter } from "@validereinc/domain";
import { Forms } from "@validereinc/domain-controllers";
import { debounce } from "es-toolkit/compat";
import React, { useMemo, useState } from "react";
import { isLookupFilterValid } from "./LookupField.helpers";

const { useValidateLookupFieldValue } = Forms.logic;

export const EquipmentFormSubmissionDropdownInput = ({
  filter,
  name,
  ...restProps
}) => {
  const { watch, setValue } = useFormContext();

  const [searchTerm, setSearchTerm] = useState("");

  const value = watch(name);

  const isValidFilterObject = useMemo(
    () => isLookupFilterValid(filter),
    [filter]
  );

  const valueOption = useQuery({
    queryKey: ["equipment", { filter, value }] as [
      string,
      { filter: any; value: any },
    ],
    queryFn: async ({ queryKey: [_entity, { filter, value }] }) => {
      if (!value) {
        return null;
      }

      const { data } = await EquipmentAdapter.getList({
        filters: {
          ...filter,
          id: value,
          isAlreadyFormatted: true,
        },
      });

      return data?.[0] ?? null;
    },
  });

  const query = useInfiniteQuery({
    queryKey: ["equipment", { filter, searchTerm }] as [
      string,
      { filter: any; searchTerm: any },
    ],
    queryFn: async ({
      queryKey: [_entity, { filter, searchTerm }],
      pageParam,
    }) =>
      await EquipmentAdapter.getList({
        page: pageParam ?? 1,
        sortBy: "name",
        sortDirection: "asc",
        pageSize: 25,
        filters: {
          ...filter,
          ...(searchTerm ? { name: { $like: searchTerm } } : {}),
          isAlreadyFormatted: true,
        },
      }),
    getNextPageParam: (lastPage) => lastPage?.page_number + 1,
    getPreviousPageParam: (prevPage) => prevPage?.page_number - 1,
    enabled: isValidFilterObject,
    staleTime: 1000 * 60 * 5,
  });

  const onSearchTermChange = debounce((newSearchTerm: string) => {
    setSearchTerm(newSearchTerm);
  }, 500);

  const onScroll = ({ isBottom }) => {
    if (isBottom) {
      query.fetchNextPage();
    }
  };

  const options = useMemo(() => {
    const selected = valueOption.data;

    const options =
      query.data?.pages?.flatMap((page) =>
        page.data.filter(({ id }) => id !== selected?.id)
      ) ?? [];

    return [...(selected ? [selected] : []), ...options];
  }, [valueOption.data, query.data]);

  useValidateLookupFieldValue({
    options,
    value,
    isLoading:
      query.isFetching ||
      valueOption.isFetching ||
      query.isLoading ||
      valueOption.isLoading,
    setValue,
    name,
  });

  return (
    <DropdownInput
      {...restProps}
      name={name}
      options={options}
      valueKey="id"
      labelKey="name"
      searchTerm={searchTerm}
      onSearchTermChange={onSearchTermChange}
      onScroll={onScroll}
      isMulti={false}
      isDisabled={!isValidFilterObject || restProps.isDisabled}
      isLoading={query.isInitialLoading}
      isBusy={query.isLoading || query.isFetchingNextPage}
      isFluid
      defaultValue={value ?? ""}
    />
  );
};
